import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Copy, ImagePlus, X, Download, PartyPopper } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { uploadPhoto } from "@/lib/upload";
import { EVENT_TYPES, getTheme, themeStyle } from "@/lib/eventTheme";
import { Logo } from "@/components/Common";
import { AIHelperButton, AIStoryCoach, AIRewriteSheet, AIPromptsButton } from "@/components/AICopilot";

const TZ = ["America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York", "UTC", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney"];
const STEPS = ["What are we gathering?", "About them", "Collection settings", "Review & share"];

export default function CreateEvent() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(null);

  const guessTz = useMemo(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } }, []);
  const [form, setForm] = useState({
    type: "memorial", honoree_name: "", relationship: "", story: "",
    date_start: "", date_end: "", event_venue: "", event_date: "", photos: [],
    deadline_date: "", deadline_time: "18:00", timezone: TZ.includes(guessTz) ? guessTz : "UTC",
    max_clip_length: 60, allow_audio: true, allow_text: true, moderation: true, pin: "",
    ai_prompts: null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const theme = getTheme(form.type);
  const [showStoryCoach, setShowStoryCoach] = useState(false);
  const [showRewrite, setShowRewrite] = useState(false);

  const onPhotos = async (files) => {
    for (const file of Array.from(files)) {
      try { const { file_id } = await uploadPhoto(file); setForm((f) => ({ ...f, photos: [...f.photos, file_id] })); }
      catch { toast.error("Could not upload photo"); }
    }
  };

  const canNext = () => {
    if (step === 0) return !!form.type;
    if (step === 1) return form.honoree_name.trim().length > 0;
    return true;
  };

  const publish = async () => {
    setSaving(true);
    try {
      let deadline = null;
      if (form.deadline_date) deadline = new Date(`${form.deadline_date}T${form.deadline_time || "18:00"}:00`).toISOString();
      const payload = {
        type: form.type, honoree_name: form.honoree_name, relationship: form.relationship, story: form.story,
        dates: { start: form.date_start || null, end: form.date_end || null },
        event_venue: form.event_venue, event_date: form.event_date, photos: form.photos,
        settings: { deadline, timezone: form.timezone, max_clip_length: Number(form.max_clip_length),
          allow_audio: form.allow_audio, allow_text: form.allow_text, moderation: form.moderation },
        pin: form.pin || null,
        prompts: form.ai_prompts && form.ai_prompts.length ? form.ai_prompts : null,
      };
      const res = await api.post("/events", payload);
      setPublished(res.data);
      toast.success("Your tribute is live");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally { setSaving(false); }
  };

  if (published) {
    const url = `${window.location.origin}/t/${published.slug}`;
    return (
      <div className="min-h-screen" style={themeStyle(form.type)}>
        <div className="max-w-xl mx-auto px-4 py-10 sm:py-16 text-center ever-fade-in">
          <div className="h-14 w-14 mx-auto grid place-items-center rounded-full" style={{ background: `hsl(${theme.accent})` }}><PartyPopper className="text-white" /></div>
          <h1 className="mt-5 font-display text-3xl font-semibold">Your tribute is ready</h1>
          <p className="mt-2 text-[hsl(var(--muted-foreground))]">Share this link or QR code. Anyone can add their voice — no account needed.</p>
          <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
            <div className="bg-white p-4 rounded-xl inline-block"><QRCodeSVG id="tribute-qr" value={url} size={168} /></div>
            <div className="mt-5 flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2 pl-4">
              <span data-testid="share-link-text" className="font-mono text-sm truncate flex-1 text-left">{url}</span>
              <button data-testid="copy-link-button" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
                className="px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold inline-flex items-center gap-1.5"><Copy size={15} /> Copy</button>
            </div>
            <button data-testid="download-qr-button" onClick={() => downloadQR(published.slug)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors"><Download size={16} /> Download QR code</button>
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={() => navigate(`/events/${published.event_id}`)} data-testid="go-to-event-button" className="flex-1 h-12 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold">Manage tribute</button>
            <a href={url} target="_blank" rel="noreferrer" className="flex-1 h-12 grid place-items-center rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors">Preview page</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={themeStyle(form.type)}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between"><Logo /><button onClick={() => navigate("/dashboard")} className="text-sm text-[hsl(var(--muted-foreground))] hover:underline">Cancel</button></div>
        {/* progress */}
        <div className="mt-6 flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={i} className="flex-1">
              <div className="h-1.5 rounded-full" style={{ background: i <= step ? `hsl(${theme.accent})` : "hsl(var(--border))" }} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Step {step + 1} of 4</p>
        <h1 className="font-display text-3xl font-semibold mt-1">{STEPS[step]}</h1>

        <div className="mt-6">
          {step === 0 && (
            <div data-testid="wizard-step-type" className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {EVENT_TYPES.map((t) => {
                const active = form.type === t.key;
                return (
                  <button key={t.key} data-testid={`event-type-${t.key}`} onClick={() => set("type", t.key)}
                    className="text-left rounded-2xl border-2 p-4 transition-colors bg-[hsl(var(--card))]"
                    style={{ borderColor: active ? `hsl(${t.accent})` : "hsl(var(--border))" }}>
                    <div className="h-10 w-10 grid place-items-center rounded-xl" style={{ background: `hsl(${t.accentSoft})`, color: `hsl(${t.accent})` }}><t.icon size={20} /></div>
                    <div className="mt-3 font-semibold text-sm leading-snug">{t.label}</div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 1 && (
            <div data-testid="wizard-step-about" className="space-y-5">
              <Field label="Their name" required>
                <input data-testid="honoree-name-input" value={form.honoree_name} onChange={(e) => set("honoree_name", e.target.value)} placeholder="Eleanor Hale" className={inputCls} />
              </Field>
              <Field label="Your relationship to them">
                <input data-testid="relationship-input" value={form.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="Granddaughter" className={inputCls} />
              </Field>
              <Field label={form.type === "memorial" ? "Their story" : "A few words about them"} helper="This appears at the top of the tribute page.">
                <div className="flex items-center gap-3 -mt-1 mb-1.5">
                  <div className="flex-1" />
                  <AIHelperButton
                    testid="ai-story-coach-button"
                    label={form.story?.trim() ? "Rewrite with AI" : "Help me write this"}
                    onClick={() => (form.story?.trim() ? setShowRewrite(true) : setShowStoryCoach(true))}
                  />
                </div>
                <textarea data-testid="story-input" value={form.story} onChange={(e) => set("story", e.target.value)} rows={5} placeholder="Share a little about who they are…" className={inputCls + " py-3 h-auto resize-none"} />
              </Field>
              {form.type === "memorial" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Born"><input data-testid="date-start-input" type="date" value={form.date_start} onChange={(e) => set("date_start", e.target.value)} className={inputCls} /></Field>
                  <Field label="Passed"><input data-testid="date-end-input" type="date" value={form.date_end} onChange={(e) => set("date_end", e.target.value)} className={inputCls} /></Field>
                </div>
              )}
              <Field label="Photos" helper="Add one or more. The first is used as the main portrait.">
                <div className="flex flex-wrap gap-3">
                  {form.photos.map((pid) => (
                    <div key={pid} className="relative h-24 w-20 rounded-xl overflow-hidden border border-[hsl(var(--border))]">
                      <img src={fileUrl(pid)} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== pid) }))} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white"><X size={13} /></button>
                    </div>
                  ))}
                  <button data-testid="add-photo-button" onClick={() => fileRef.current?.click()} className="h-24 w-20 grid place-items-center rounded-xl border-2 border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-colors"><ImagePlus size={20} /></button>
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPhotos(e.target.files)} />
                </div>
              </Field>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-settings" className="space-y-5">
              <Field label="Submission deadline" helper="Contributors can record until this time.">
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="deadline-date-input" type="date" value={form.deadline_date} onChange={(e) => set("deadline_date", e.target.value)} className={inputCls} />
                  <input data-testid="deadline-time-input" type="time" value={form.deadline_time} onChange={(e) => set("deadline_time", e.target.value)} className={inputCls} />
                </div>
                <select data-testid="timezone-select" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} className={inputCls + " mt-3"}>
                  {TZ.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Maximum clip length">
                <select data-testid="max-length-select" value={form.max_clip_length} onChange={(e) => set("max_clip_length", e.target.value)} className={inputCls}>
                  {[30, 45, 60, 90, 120].map((s) => <option key={s} value={s}>{s} seconds</option>)}
                </select>
              </Field>
              <Toggle testid="toggle-audio" label="Allow audio-only tributes" desc="For those who prefer not to be on camera." value={form.allow_audio} onChange={(v) => set("allow_audio", v)} accent={theme.accent} />
              <Toggle testid="toggle-text" label="Allow written tributes" desc="A typed message with an optional photo." value={form.allow_text} onChange={(v) => set("allow_text", v)} accent={theme.accent} />
              <Toggle testid="toggle-moderation" label="Review before showing" desc="You approve each tribute before it's included." value={form.moderation} onChange={(v) => set("moderation", v)} accent={theme.accent} />
              <Field label="Optional PIN" helper="Require a code to open the tribute page. Leave blank for none.">
                <input data-testid="pin-input" value={form.pin} onChange={(e) => set("pin", e.target.value)} placeholder="e.g. 1234" className={inputCls} />
              </Field>
              <AIPromptsButton
                eventType={form.type}
                honoreeName={form.honoree_name}
                story={form.story}
                onApply={(prompts) => { set("ai_prompts", prompts); toast.success("Personalized prompts saved. Contributors will see these on the tribute page."); }}
              />
              {form.ai_prompts && (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {form.ai_prompts.length} personalized prompts saved. <button className="underline" onClick={() => set("ai_prompts", null)}>Clear</button>
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-review" className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
              <div className="flex items-center gap-4">
                {form.photos[0] ? <img src={fileUrl(form.photos[0])} alt="" className="h-20 w-16 rounded-xl object-cover" /> :
                  <div className="h-20 w-16 rounded-xl grid place-items-center" style={{ background: `hsl(${theme.accentSoft})` }}><theme.icon style={{ color: `hsl(${theme.accent})` }} /></div>}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: `hsl(${theme.accent})` }}>{theme.short}</div>
                  <div className="font-display text-2xl font-semibold">{theme.framing} {form.honoree_name || "…"}</div>
                </div>
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <Info label="Deadline" value={form.deadline_date ? `${form.deadline_date} ${form.deadline_time}` : "None"} />
                <Info label="Max clip" value={`${form.max_clip_length}s`} />
                <Info label="Audio-only" value={form.allow_audio ? "Allowed" : "Off"} />
                <Info label="Written" value={form.allow_text ? "Allowed" : "Off"} />
                <Info label="Moderation" value={form.moderation ? "On" : "Off"} />
                <Info label="PIN" value={form.pin ? "Set" : "None"} />
              </dl>
              <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">Publishing creates a private, unlisted link and a QR code you can share.</p>
            </div>
          )}
        </div>
      </div>

      {/* AI copilot modals */}
      <AIStoryCoach
        open={showStoryCoach}
        onClose={() => setShowStoryCoach(false)}
        eventType={form.type}
        honoreeName={form.honoree_name}
        relationship={form.relationship}
        onApply={({ story }) => { set("story", story); toast.success("Draft loaded. Edit anything before publishing."); }}
      />
      <AIRewriteSheet
        open={showRewrite}
        onClose={() => setShowRewrite(false)}
        currentText={form.story}
        context={{ honoree_name: form.honoree_name, event_type: form.type }}
        onApply={(text) => { set("story", text); toast.success("Draft loaded. Edit anything before publishing."); }}
      />

      {/* sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {step > 0 && <button data-testid="wizard-back-button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors"><ArrowLeft size={16} /> Back</button>}
          <div className="flex-1" />
          {step < 3 ? (
            <button data-testid="wizard-next-button" disabled={!canNext()} onClick={() => canNext() && setStep((s) => s + 1)}
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">Continue <ArrowRight size={16} /></button>
          ) : (
            <button data-testid="wizard-publish-button" disabled={saving} onClick={publish}
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"><Check size={16} /> {saving ? "Publishing…" : "Publish tribute"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-12 rounded-xl px-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";
const Field = ({ label, helper, required, children }) => (
  <div><label className="text-sm font-medium">{label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}</label>{helper && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 mb-1.5">{helper}</p>}<div className={helper ? "" : "mt-1.5"}>{children}</div></div>
);
const Info = ({ label, value }) => (<div><dt className="text-[hsl(var(--muted-foreground))]">{label}</dt><dd className="font-semibold">{value}</dd></div>);
const Toggle = ({ label, desc, value, onChange, accent, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onChange(!value)} className="w-full flex items-center justify-between gap-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 text-left">
    <div><div className="font-semibold text-sm">{label}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{desc}</div></div>
    <span className="h-6 w-11 rounded-full transition-colors relative shrink-0" style={{ background: value ? `hsl(${accent})` : "hsl(var(--border))" }}>
      <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all" style={{ left: value ? "22px" : "2px" }} />
    </span>
  </button>
);

function downloadQR(slug) {
  const svg = document.getElementById("tribute-qr");
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, 512, 512);
    ctx.drawImage(img, 32, 32, 448, 448);
    const a = document.createElement("a");
    a.download = `gathervoices-${slug}.png`; a.href = canvas.toDataURL("image/png"); a.click();
  };
  img.src = svg64;
}
